import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    ShieldAlert, Trash2, Mail, Phone, MessageSquare,
    CheckCircle2, Loader2, Smartphone, Info, Shield, ArrowRight, Sparkles
} from "lucide-react";
import FemcareLogo from "../components/FemcareLogo";
import { api } from "../lib/api";
import { useReveal, useSpotlight } from "../lib/motion";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function DeleteAccount() {
    const heroRef = useRef(null);
    useSpotlight(heroRef);
    useReveal();

    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [reason, setReason] = useState("");
    const [confirm, setConfirm] = useState(false);
    const [touched, setTouched] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState("");

    const emailValid = EMAIL_RE.test(email.trim());
    const canSubmit = emailValid && confirm && !submitting;

    const onSubmit = async (e) => {
        e.preventDefault();
        setTouched(true);
        setError("");
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            await api.post("/account/delete-request", {
                email: email.trim(),
                phone: phone.trim() || null,
                reason: reason.trim() || null,
                confirm: true,
            });
            setSubmitted(true);
        } catch (err) {
            const status = err?.response?.status;
            if (status === 429) {
                setError("Too many requests from this network. Please try again later or email support@femcare.life.");
            } else {
                setError("We couldn't submit your request just now. Please try again in a moment.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-femcare-cream" data-testid="delete-account-page">
            {/* HERO */}
            <section ref={heroRef} className="relative overflow-hidden spotlight">
                <div className="aurora grain" aria-hidden="true" />
                <div className="max-w-3xl mx-auto px-6 lg:px-12 pt-24 pb-20 relative">
                    <div className="flex items-center gap-4 mb-10 reveal">
                        <div className="f-coin">
                            <FemcareLogo className="w-9 h-9" />
                        </div>
                        <span className="text-xs uppercase tracking-[0.35em] text-femcare-accent font-medium">
                            Account &amp; Data
                        </span>
                    </div>
                    <h1
                        className="editorial-headline text-4xl sm:text-5xl lg:text-6xl text-femcare-purple leading-none mb-8 reveal reveal-delay-1"
                        data-testid="delete-account-heading"
                    >
                        Delete your<br />
                        <span className="italic font-light">Femcare</span> account.
                    </h1>
                    <p className="text-lg text-femcare-purple/70 leading-relaxed max-w-2xl reveal reveal-delay-2">
                        You can permanently delete your Femcare account and the personal data associated
                        with it — from inside the app, or right here on the web if you no longer have access.
                    </p>
                </div>
            </section>

            <div className="max-w-3xl mx-auto px-6 lg:px-12 pb-24 space-y-8 -mt-8 relative z-10">

                {/* IN-APP OPTION — glass hero card */}
                <section
                    className="glass rounded-3xl p-6 md:p-8 reveal"
                    data-testid="delete-inapp-section"
                >
                    <div className="flex items-center gap-3 mb-3">
                        <span className="w-10 h-10 rounded-full bg-femcare-petal flex items-center justify-center">
                            <Smartphone className="w-4 h-4 text-femcare-pink" />
                        </span>
                        <h2 className="font-serif text-2xl md:text-3xl text-femcare-purple">
                            Delete from inside the app
                        </h2>
                    </div>
                    <p className="text-femcare-purple/80 leading-relaxed">
                        If you still have access to the Femcare app on your phone, the fastest way to delete your
                        account is directly from your profile:
                    </p>
                    <div className="mt-5 flex flex-wrap items-center gap-2 text-femcare-purple font-medium">
                        <span className="bg-white/80 border border-femcare-border px-3 py-1.5 rounded-full text-sm">Profile</span>
                        <ArrowRight className="w-4 h-4 text-femcare-pink" />
                        <span className="bg-white/80 border border-femcare-border px-3 py-1.5 rounded-full text-sm">Danger Zone</span>
                        <ArrowRight className="w-4 h-4 text-femcare-pink" />
                        <span className="bg-femcare-pink text-white px-3 py-1.5 rounded-full text-sm">
                            Delete Account
                        </span>
                    </div>
                    <p className="mt-4 text-sm text-femcare-purple/60">
                        You&apos;ll be asked to confirm with your password before deletion is finalised.
                    </p>
                </section>

                {/* WEB FORM — solid editorial card (form clarity > glass) */}
                <section
                    className="card-editorial p-6 md:p-8 reveal reveal-delay-1"
                    data-testid="delete-web-section"
                >
                    <div className="flex items-center gap-3 mb-3">
                        <span className="w-10 h-10 rounded-full bg-femcare-petal flex items-center justify-center">
                            <ShieldAlert className="w-4 h-4 text-femcare-pink" />
                        </span>
                        <h2 className="font-serif text-2xl md:text-3xl text-femcare-purple">
                            Can&apos;t access the app?
                        </h2>
                    </div>
                    <p className="text-femcare-purple/80 leading-relaxed">
                        Submit a deletion request below and our support team will verify ownership of the account
                        before permanently removing your data. You do <b>not</b> need to reinstall the app.
                    </p>

                    {submitted ? (
                        <div
                            className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-6 flex gap-4 items-start"
                            role="status"
                            data-testid="delete-success"
                        >
                            <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
                            <div>
                                <h3 className="font-serif text-xl text-emerald-800 mb-1">
                                    Account deletion request received
                                </h3>
                                <p className="text-emerald-900/85 leading-relaxed">
                                    Your request to delete your Femcare account has been received. We&apos;ve
                                    sent a confirmation link to your email — tap it within 7 days to verify
                                    you own this account. We may also contact you to verify ownership
                                    before processing the deletion request.
                                </p>
                                <p className="mt-3 text-sm text-emerald-900/70">
                                    Questions? Email{" "}
                                    <a
                                        href="mailto:support@femcare.life"
                                        className="underline decoration-emerald-500/40 hover:decoration-emerald-700"
                                    >
                                        support@femcare.life
                                    </a>
                                    .
                                </p>
                            </div>
                        </div>
                    ) : (
                        <form
                            onSubmit={onSubmit}
                            noValidate
                            className="mt-6 space-y-5"
                            data-testid="delete-request-form"
                        >
                            {/* Email */}
                            <label className="block">
                                <span className="text-sm font-medium text-femcare-purple">
                                    Account email address <span className="text-femcare-pink">*</span>
                                </span>
                                <div className="mt-1.5 relative">
                                    <Mail className="w-4 h-4 text-femcare-purple/40 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                                    <input
                                        type="email"
                                        required
                                        autoComplete="email"
                                        inputMode="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        onBlur={() => setTouched(true)}
                                        placeholder="you@example.com"
                                        aria-invalid={touched && !emailValid}
                                        className="w-full pl-11 pr-4 py-3 rounded-xl border border-femcare-border bg-femcare-cream/40 focus:bg-white focus:outline-none focus:border-femcare-pink focus:ring-2 focus:ring-femcare-pink/20 text-femcare-purple placeholder-femcare-purple/30"
                                        data-testid="delete-email-input"
                                    />
                                </div>
                                {touched && !emailValid && (
                                    <p className="mt-1.5 text-xs text-red-600" data-testid="delete-email-error">
                                        Please enter a valid email address.
                                    </p>
                                )}
                            </label>

                            {/* Phone */}
                            <label className="block">
                                <span className="text-sm font-medium text-femcare-purple">
                                    Phone number <span className="text-femcare-purple/50">(optional)</span>
                                </span>
                                <div className="mt-1.5 relative">
                                    <Phone className="w-4 h-4 text-femcare-purple/40 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                                    <input
                                        type="tel"
                                        autoComplete="tel"
                                        inputMode="tel"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        placeholder="+91 98xxxxxx"
                                        className="w-full pl-11 pr-4 py-3 rounded-xl border border-femcare-border bg-femcare-cream/40 focus:bg-white focus:outline-none focus:border-femcare-pink focus:ring-2 focus:ring-femcare-pink/20 text-femcare-purple placeholder-femcare-purple/30"
                                        data-testid="delete-phone-input"
                                    />
                                </div>
                            </label>

                            {/* Reason */}
                            <label className="block">
                                <span className="text-sm font-medium text-femcare-purple">
                                    Reason for deletion <span className="text-femcare-purple/50">(optional)</span>
                                </span>
                                <div className="mt-1.5 relative">
                                    <MessageSquare className="w-4 h-4 text-femcare-purple/40 absolute left-4 top-4 pointer-events-none" />
                                    <textarea
                                        rows={4}
                                        value={reason}
                                        maxLength={2000}
                                        onChange={(e) => setReason(e.target.value)}
                                        placeholder="Optional — tell us anything we can improve."
                                        className="w-full pl-11 pr-4 py-3 rounded-xl border border-femcare-border bg-femcare-cream/40 focus:bg-white focus:outline-none focus:border-femcare-pink focus:ring-2 focus:ring-femcare-pink/20 text-femcare-purple placeholder-femcare-purple/30 resize-y"
                                        data-testid="delete-reason-input"
                                    />
                                </div>
                            </label>

                            {/* Confirmation */}
                            <label className="flex items-start gap-3 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={confirm}
                                    onChange={(e) => setConfirm(e.target.checked)}
                                    className="mt-1 w-4 h-4 accent-femcare-pink"
                                    data-testid="delete-confirm-checkbox"
                                />
                                <span className="text-sm text-femcare-purple/85 leading-relaxed">
                                    I understand that I am requesting permanent deletion of my Femcare account and
                                    associated personal data.
                                </span>
                            </label>

                            {/* Error */}
                            {error && (
                                <div
                                    role="alert"
                                    className="rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm p-3"
                                    data-testid="delete-error"
                                >
                                    {error}
                                </div>
                            )}

                            {/* Submit */}
                            <button
                                type="submit"
                                disabled={!canSubmit}
                                className="inline-flex items-center justify-center gap-2 w-full sm:w-auto bg-femcare-pink hover:bg-femcare-pink/90 disabled:bg-femcare-pink/40 disabled:cursor-not-allowed text-white rounded-full px-7 py-3.5 font-medium magnet"
                                data-testid="delete-submit-btn"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Submitting…
                                    </>
                                ) : (
                                    <>
                                        <Trash2 className="w-4 h-4" />
                                        Request account deletion
                                    </>
                                )}
                            </button>

                            <p className="text-xs text-femcare-purple/55 leading-relaxed">
                                Submitting this form does <b>not</b> immediately delete your account. Our support
                                team will verify that you are the account owner before any data is removed.
                            </p>
                        </form>
                    )}
                </section>

                {/* WHAT HAPPENS TO YOUR DATA */}
                <section
                    className="card-editorial p-6 md:p-8 reveal reveal-delay-2"
                    data-testid="delete-data-section"
                >
                    <div className="flex items-center gap-3 mb-3">
                        <span className="w-10 h-10 rounded-full bg-femcare-petal flex items-center justify-center">
                            <Info className="w-4 h-4 text-femcare-pink" />
                        </span>
                        <h2 className="font-serif text-2xl md:text-3xl text-femcare-purple">
                            What happens to your data?
                        </h2>
                    </div>
                    <p className="text-femcare-purple/80 leading-relaxed">
                        When your Femcare account is deleted, the personal data associated with your account
                        will be deleted, subject to any information Femcare is required to retain for legal,
                        security, fraud-prevention, regulatory, or other legitimate compliance requirements.
                    </p>
                    <p className="mt-4 text-femcare-purple/80 leading-relaxed">
                        For full details on what we store, why, and for how long, please review our{" "}
                        <Link
                            to="/privacy"
                            className="text-femcare-pink underline decoration-femcare-pink/30 hover:decoration-femcare-pink underline-offset-4"
                            data-testid="delete-privacy-link"
                        >
                            Privacy Policy
                        </Link>
                        .
                    </p>
                </section>

                {/* AFFILIATE MICRO-BAND */}
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-full bg-white/70 border border-femcare-border px-5 py-3 text-sm text-femcare-purple/75 reveal reveal-delay-3">
                    <span className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-femcare-pink" />
                        Not leaving forever? Explore our partner program.
                    </span>
                    <Link
                        to="/affiliate"
                        className="text-femcare-pink font-medium hover:underline decoration-femcare-pink/40 underline-offset-4"
                    >
                        Become a partner →
                    </Link>
                </div>

                {/* FOOTER LINKS */}
                <div className="flex flex-wrap items-center justify-center gap-4 pt-2 text-sm text-femcare-purple/70">
                    <Link
                        to="/privacy"
                        className="inline-flex items-center gap-1.5 hover:text-femcare-pink"
                    >
                        <Shield className="w-3.5 h-3.5" /> Privacy Policy
                    </Link>
                    <span className="text-femcare-purple/20">·</span>
                    <a
                        href="mailto:support@femcare.life"
                        className="inline-flex items-center gap-1.5 hover:text-femcare-pink"
                    >
                        <Mail className="w-3.5 h-3.5" /> support@femcare.life
                    </a>
                </div>
            </div>
        </div>
    );
}
